import { type MouseEvent, useEffect, useRef, useState } from "react";
import {
  contactBackground,
  labelBookings,
  labelMgmt,
  labelPr,
  menuContact1Crop as menuContact1,
  signupPoster,
  signupSpeechBubble,
  signupVideo,
  skyBakgrund,
} from "../assets";
import { SignupModal } from "../components/SignupModal";
import { HomeButton, MuteToggle, SiteNav } from "../components/SiteChrome";
import { SocialIcons } from "../components/SocialIcons";
import { useBackgroundWrapper } from "../useBackgroundWrapper";
import "./Contact.css";

/**
 * Each address, and the hand-lettered word that names it. Ticket 026: the
 * lettering used to be painted into `sky_bakgrund` and the addresses pinned
 * underneath it in percentages of the canvas, which is what locked this page to
 * one screen — the text could not move without coming unstuck from its label.
 * As three separate drawings, a label and its address are one row of a column.
 *
 * `slug` is what src/mobile/portrait-contact.css sizes each label by: they are
 * three different
 * shapes and are scaled to one hand, not to one width.
 */
const CONTACTS = [
  {
    id: "booking-mail-text",
    slug: "bookings",
    email: "pontus.sillren@luger.se",
    label: labelBookings,
    alt: "Bookings",
  },
  { id: "pr-mail-text", slug: "pr", email: "chloe.hadded@wma.agency", label: labelPr, alt: "PR" },
  {
    id: "wife-mail-text",
    slug: "mgmt",
    email: "river@wifesthlm.com",
    label: labelMgmt,
    alt: "Mgmt",
  },
];

function copyToClipboard(text: string) {
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text).catch(() => fallbackCopy(text));
  } else {
    fallbackCopy(text);
  }
}

function fallbackCopy(text: string) {
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.style.position = "fixed";
  ta.style.left = "-9999px";
  document.body.appendChild(ta);
  ta.focus();
  ta.select();
  try {
    document.execCommand("copy");
  } catch {
    /* nothing else to try */
  }
  document.body.removeChild(ta);
}

export function Contact() {
  const { wrapperRef, imageRef, onLoad, wrapperClassName, wrapperStyle } =
    useBackgroundWrapper(skyBakgrund);
  const [signupOpen, setSignupOpen] = useState(false);
  /** where to park the "copied" toast, in wrapper-relative px */
  const [toast, setToast] = useState<{ left: number; top: number } | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => () => clearTimeout(toastTimer.current), []);

  // Clicking an address copies it *and* still opens the mail client, so both
  // habits work.
  function onMailClick(e: MouseEvent<HTMLAnchorElement>, email: string) {
    copyToClipboard(email);
    const rect = e.currentTarget.getBoundingClientRect();
    const wrapperRect = wrapperRef.current!.getBoundingClientRect();
    setToast({
      left: rect.left - wrapperRect.left + rect.width / 2,
      top: rect.top - wrapperRect.top,
    });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 1400);
  }

  return (
    <div className="page-contact">
      {/* The backdrop the stack floats on, outside the wrapper so it covers the
          viewport rather than the artwork and does not wait on the wrapper's
          fade — /watch's #watch-sky, with this page's own art. Not the same file
          as #bg-image below: `sky_bakgrund` has BOOKINGS, PR and MGMT painted
          into it and this one has no lettering at all, which is the whole reason
          the page can become a column. So it is a second cache entry, and
          routePrewarm decodes it with the rest of /contact's artwork.

          It is also a portrait picture rather than a landscape one — 426 x 980,
          the cherub painted at the shape a phone actually shows — so `cover`
          crops it barely at all and scales it down rather than up. See
          src/mobile/portrait-contact.css.

          `hidden` rather than a `display: none` in Contact.css: the attribute is
          the UA stylesheet's own `display: none`, which any author rule
          outranks, so src/mobile/portrait-contact.css turns it on for the one viewport
          that wants
          it — a portrait phone — and every other viewport gets it off for free.
          Ticket 026. */}
      <img id="contact-sky" src={contactBackground} alt="" hidden />
      <div id="bg-wrapper" className={wrapperClassName} style={wrapperStyle} ref={wrapperRef}>
        <img
          id="bg-image"
          className="bg-image"
          ref={imageRef}
          onLoad={onLoad}
          src={skyBakgrund}
          alt=""
        />
        <img id="contact-title" src={menuContact1} alt="Contact" />
        {/* Label and address together, because on a phone they move together.
            `.contact-row` is `display: contents` everywhere else (Contact.css),
            so the wrapper generates no box and the <a> inside it goes on
            positioning against #bg-wrapper at exactly the top/left it always
            did — the same trick `.social-row` plays in global.css. Ticket 026. */}
        {CONTACTS.map(({ id, slug, email, label, alt }) => (
          <div key={id} className="contact-row">
            <img className={`contact-label label-${slug}`} src={label} alt={alt} hidden />
            <a
              id={id}
              className="mail-text"
              href={`mailto:${email}`}
              draggable={false}
              onClick={(e) => onMailClick(e, email)}
              // Ticket 028, extended 2026-08-19: the three addresses copy to
              // the clipboard as often as they open a mail client, so a press
              // here usually leaves the visitor on the page. They keep the loop.
              data-audio="keep"
            >
              {email}
            </a>
          </div>
        ))}
        <div
          id="copy-toast"
          className={toast ? "show" : undefined}
          style={toast ? { left: `${toast.left}px`, top: `${toast.top}px` } : undefined}
        >
          Kopierad!
        </div>
        {/* The two ways into the mailing-list modal are one composition: the
            bubble hangs off the envelope's top-right corner and the *overlap* is
            the point, so a flow column would stack them into two unrelated rows.
            Wrapped for the same reason and in the same way as a contact row —
            `display: contents` off a portrait phone, so desktop is untouched. */}
        {/* Ticket 028 exempts the whole signup flow, the two triggers
            included: the tap that opens the modal is the only one in the flow
            that could mute, so sparing the modal alone would spare nothing. */}
        <div id="signup-row" data-audio="keep">
          <img
            id="signup-speech-bubble"
            src={signupSpeechBubble}
            alt=""
            onClick={() => setSignupOpen(true)}
          />
          <video
            id="signup-envelope-trigger"
            className="interactive"
            src={signupVideo}
            poster={signupPoster}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            onClick={() => setSignupOpen(true)}
          />
        </div>
        <SocialIcons />
      </div>

      <HomeButton />
      <MuteToggle />
      <SiteNav active="/contact" />

      <SignupModal open={signupOpen} onClose={() => setSignupOpen(false)} />
    </div>
  );
}
